<template>
  <div class="wrapper">
    <ion-app>
      <div class="custom-header">
        <div :class="{ 'active-tabs': tabRoute }">
          <ion-router-outlet :animated="false" />
        </div>
        <tabs v-if="tabRoute" :route="nextRoute" />
        <button v-if="canInstallPwa" class="pwa-install" @click="installPwa">
          Instalar app
        </button>
      </div>
    </ion-app>
  </div>
</template>

<script>
import { Capacitor } from "@capacitor/core";

import { IonApp, IonRouterOutlet } from "@ionic/vue";
import { defineComponent, onBeforeUnmount, onMounted } from "vue";

import { useStore } from "vuex";
import { computed, watch } from "vue";
import { ref } from "vue";
import { useRoute } from "vue-router";
import { Preferences } from "@capacitor/preferences";

import { StatusBar, Style } from "@capacitor/status-bar";

import Tabs from "@/components/Tabs.vue";

export default defineComponent({
  name: "App",
  components: {
    IonApp,
    Tabs,
    IonRouterOutlet,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const tabRoute = ref(false);
    const nextRoute = ref(null);
    const deferredInstallPrompt = ref(null);
    const canInstallPwa = ref(false);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredInstallPrompt.value = event;
      canInstallPwa.value = true;
    };

    const handleAppInstalled = () => {
      deferredInstallPrompt.value = null;
      canInstallPwa.value = false;
    };

    const installPwa = async () => {
      if (!deferredInstallPrompt.value) return;

      deferredInstallPrompt.value.prompt();
      await deferredInstallPrompt.value.userChoice.catch(() => null);
      deferredInstallPrompt.value = null;
      canInstallPwa.value = false;
    };

    onMounted(async () => {
      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.addEventListener("appinstalled", handleAppInstalled);

      if (Capacitor.isNativePlatform()) {
        StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
        StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
        StatusBar.setBackgroundColor({ color: "#245b42" }).catch(() => {});
      }

      let token = await Preferences.get({ key: "_userToken" });
      if (token.value) store.commit("_LOGIN", JSON.parse(token.value));

      let sensor = await Preferences.get({ key: "_sensor" });
      if (sensor.value) store.commit("_SENSOR", sensor.value);

      if (token.value) {
        // get job terms
        await store.dispatch("_getJobTerms");
        // get jobs
        await store.dispatch("_jobsReload");
        // get Clients
        await store.dispatch("_getClients", 1);
        // get Payments
        await store.dispatch("_getPayments", 1);
      }

      store.commit("_CURRENT_ROUTE", route.name);
    });

    watch(route, (value) => {
      store.commit("_CURRENT_ROUTE", value.name);
    });

    const getRoutes = computed(() => store.state.currentRoute);

    watch(getRoutes, (currentRoute) => {
      if (
        currentRoute == "home" ||
        currentRoute == "jobs" ||
        currentRoute == "clients" ||
        currentRoute == "job-terms" ||
        currentRoute == "payments" ||
        currentRoute == "trackers"
      ) {
        tabRoute.value = true;
      } else {
        tabRoute.value = false;
      }
      setTimeout(() => {
        nextRoute.value = currentRoute;
      }, 10);
    });

    onBeforeUnmount(() => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    });

    return {
      tabRoute,
      getRoutes,
      nextRoute,
      canInstallPwa,
      installPwa,
    };
  },
});
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700;800&display=swap");
@import "./theme/theme.scss";

* {
  font-family: "Rubik", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif !important;
  letter-spacing: 0;
}

body {
  font-weight: 400;
  text-rendering: geometricPrecision;
}

:root {
  --ion-color-primary: #4f8c42 !important;
  --ion-color-secondary-rgb: 0, 102, 0;
  --ion-color-secondary-contrast: #ffffff;
  --ion-color-secondary-contrast-rgb: 255, 255, 255;
  --ion-color-secondary-shade: #005a00;
  --ion-color-secondary-tint: #1a751a;
}

.active-tabs div.ion-page {
  bottom: 60px !important;
}

@import "@/theme/theme.scss";
.toolbar-title-default {
  box-shadow: none !important;
}

.custom-header {
  // background-image: linear-gradient(120deg, $primary, $secondary);
  background: $secondary;
  color: white;
}

.custom-card {
  margin: 0 0 20px 0 !important;
}

.click-to-call {
  padding: 0 !important;
}

.floated {
  .custom-card {
    margin-right: 10px !important;
  }
}

.mapboxgl-canvas {
  border-radius: 10px;
}

.item-native {
  padding-left: 0 !important;
}

ion-item {
  --ripple-color: rgba(47, 143, 82, 0.22);
}

ion-item::part(native),
ion-button::part(native) {
  transition: background-color 120ms ease, transform 120ms ease,
    opacity 120ms ease;
}

ion-item:active::part(native) {
  background: rgba(47, 143, 82, 0.08);
}

ion-button:active::part(native) {
  transform: scale(0.97);
}

ion-toast {
  z-index: 2147483647 !important;
}

ion-toast::part(container) {
  z-index: 2147483647 !important;
}

ion-modal,
ion-alert,
ion-action-sheet,
ion-popover,
ion-loading,
ion-picker {
  z-index: 100000 !important;
}

.pwa-install {
  position: fixed;
  right: 16px;
  bottom: calc(76px + env(safe-area-inset-bottom));
  z-index: 99999;
  border: 0;
  border-radius: 999px;
  padding: 10px 16px;
  background: #245b42;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 10px 24px rgba(20, 38, 29, 0.24);
}
</style>
